import { FunctionComponent, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useQueryParam, useValidatedParam } from 'core/hooks';
import { hasText } from 'core/utils';

import { AuthenticationScheme, useAuthentication, useParsedShareAccessToken } from 'features/auth';

export const ShareLandingAuthorizationChallenge: FunctionComponent = () => {
  const { activeScheme, logoutShare } = useAuthentication();
  const navigate = useNavigate();
  const returnRoute = useQueryParam('return', true);
  const linkId = useValidatedParam('linkId', 'guid', true);
  const shareToken = useParsedShareAccessToken(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    // This component is the "challenge" for the landing page route.
    // We only end up here if the RouteGuard determined the user is not in an allowed scheme.
    // For the landing page, this means the user is already in a SHARE session.
    if (activeScheme === AuthenticationScheme.SHARE) {
      // If the linkId in the token doesn't match the linkId in the URL,
      // it means we are switching from one share to another, so we must log out first.
      if (shareToken != null && shareToken.shareLinkId !== linkId) {
        setIsLoggingOut(true);
        logoutShare();
      } else if (shareToken != null && shareToken.shareLinkId === linkId) {
        // The user is already logged into the correct share, navigate to the viewer.
        navigate(hasText(returnRoute) ? returnRoute : `/share/${linkId}/viewer`);
      }
    }
  }, [activeScheme, linkId, logoutShare, navigate, returnRoute, shareToken]);

  useEffect(() => {
    // This effect waits for the logout process to complete.
    // Once the activeScheme is no longer SHARE, we can stop the logout process.
    if (isLoggingOut && activeScheme !== AuthenticationScheme.SHARE) {
      setIsLoggingOut(false);
    }
  }, [activeScheme, isLoggingOut]);

  // Render nothing while the logout is in progress.
  if (isLoggingOut) {
    return null;
  }

  // Render nothing by default. The RouteGuard will re-render the correct component once the state is right.
  return null;
};

ShareLandingAuthorizationChallenge.displayName = 'ShareLandingAuthorizationChallenge';
